import { Check, Link2, MessageCircle, Send, Share2, X } from 'lucide-react';
import { type ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { formatMoney } from '../../lib/money.js';

import { StoreImage } from './StoreImage.js';

// lucide-react dropped brand glyphs — inline the Facebook + X marks.
function FacebookGlyph({ className }: { className?: string }): JSX.Element {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.09 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.09 24 18.1 24 12.07Z" /></svg>;
}
function XGlyph({ className }: { className?: string }): JSX.Element {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" /></svg>;
}

export interface ShareProduct {
  id: string;
  slug: string | null;
  name: string;
  price: string;
  currencyCode: string;
  imageUrl: string | null;
}

/**
 * Pro share sheet for a product — a brand-accented modal (matching the search
 * palette) with a copy-link field + WhatsApp / Facebook / X / Telegram buttons.
 * Reused by product cards and the product page.
 */
export function StorefrontShareModal({
  slug,
  product,
  open,
  onClose,
}: {
  slug: string;
  product: ShareProduct;
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const { t, i18n } = useTranslation('cms');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setCopied(false);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  const url = `${window.location.origin}/s/${slug}/product/${product.slug ?? product.id}`;
  const priceText = formatMoney(product.price, { locale: i18n.language, currency: product.currencyCode });
  const text = `${product.name} — ${priceText}`;

  const copy = (): void => {
    void navigator.clipboard?.writeText(url)
      .then(() => { setCopied(true); toast.success(t('store.product.linkCopied', { defaultValue: 'Link copied' })); })
      .catch(() => undefined);
  };

  const channels: Array<{ key: string; label: string; Icon: ComponentType<{ className?: string }>; hover: string; href: string }> = [
    { key: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, hover: 'hover:border-success/50 hover:text-success', href: `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}` },
    { key: 'facebook', label: 'Facebook', Icon: FacebookGlyph, hover: 'hover:border-blue-500/50 hover:text-blue-500', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { key: 'x', label: 'X', Icon: XGlyph, hover: 'hover:border-fg/40 hover:text-fg', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
    { key: 'telegram', label: 'Telegram', Icon: Send, hover: 'hover:border-sky-500/50 hover:text-sky-500', href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-md duration-200 animate-in fade-in-0"
      role="dialog"
      aria-modal="true"
      aria-label={t('store.product.share', { defaultValue: 'Share' })}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-brand/30 bg-bg shadow-2xl shadow-brand/10 ring-1 ring-brand/20 duration-200 animate-in fade-in-0 zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border bg-gradient-to-b from-brand/[0.06] to-transparent px-4 py-3">
          <h2 className="flex items-center gap-2 text-sm font-bold"><Share2 className="h-4 w-4 text-brand" aria-hidden />{t('store.product.shareTitle', { defaultValue: 'Share this product' })}</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-fg-muted transition hover:bg-brand/10 hover:text-fg" aria-label={t('store.product.close', { defaultValue: 'Close' })}><X className="h-4 w-4" aria-hidden /></button>
        </div>

        <div className="flex items-center gap-3 p-4">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-bg-muted"><StoreImage src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" /></div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{product.name}</p>
            <p className="font-mono text-sm font-bold tabular-nums text-brand">{priceText}</p>
          </div>
        </div>

        <div className="px-4">
          <div className="flex items-center gap-2 rounded-xl border border-border bg-bg-subtle p-1.5">
            <span className="min-w-0 flex-1 truncate px-2 text-xs text-fg-muted" dir="ltr">{url}</span>
            <button type="button" onClick={copy} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-brand-contrast transition hover:opacity-90 active:scale-95">
              {copied ? <Check className="h-4 w-4" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
              {copied ? t('store.product.copied', { defaultValue: 'Copied' }) : t('store.product.copy', { defaultValue: 'Copy' })}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 p-4">
          {channels.map(({ key, label, Icon, hover, href }) => (
            <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className={`flex flex-col items-center gap-1.5 rounded-xl border border-border p-3 text-fg-muted transition active:scale-95 ${hover}`}>
              <Icon className="h-6 w-6" />
              <span className="text-[0.65rem] font-medium">{label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
